import styled from "@emotion/styled";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { ChevronDownIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { Popper } from "Components/_Shared/Popper";
import {
  getGetGoalModelingAssetOptionsQueryKey,
  getGetGoalModelingDetailQueryKey,
  getGetModelingAvailableAssetAmountQueryKey,
  getGetModelingRecapitulationQueryKey,
  useCreateGoalModelingAllocatedAmount,
  useGetGoalModelingAssetOptions,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import type { CreateAllocatedAmountBodySourceType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency, formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

type Props = {
  readonly goalId: string;
};

const StyledItem = styled(Stack)`
  width: 356px;

  &:hover {
    cursor: pointer;
    background: ${colors.backgroundSecondary};
  }
`;

export const CoverageSelect: FC<Props> = ({ goalId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const [open, setOpen] = useState(false);

  const { data: assetOptions } = useGetGoalModelingAssetOptions(analysisId, versionId, goalId, {
    query: { enabled: open },
  });

  const { mutate } = useCreateGoalModelingAllocatedAmount();

  const nonZeroAssetOptions = assetOptions?.items.filter((ao) => (ao.remainingAmount?.value ?? 0) !== 0);

  return (
    <Box display="flex" justifyContent="center">
      <Popper
        isOpen={open}
        noPadding
        placement="bottom"
        onOpen={() => {
          queryClient.invalidateQueries({ queryKey: getGetGoalModelingAssetOptionsQueryKey(analysisId, versionId, goalId) });
          setOpen(true);
        }}
        btnEl={
          <Button>
            <Typography color={theme.palette.primary.main} variant="body2" fontWeight={600} mr={Spacing.S}>
              {t(Resources.AnalysisDetail.Modeling.SelectCoverage)}
            </Typography>
            <ChevronDownIcon sx={{ color: "primary.main" }} />
          </Button>
        }
      >
        <Stack p={Spacing.M}>
          {nonZeroAssetOptions?.length === 0 && (
            <StyledItem padding={Spacing.M} spacing={Spacing.S}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="secondary">
                  {t(Resources.AnalysisDetail.Modeling.NoAssetsToDistribute)}
                </Typography>
              </Box>
            </StyledItem>
          )}
          {!!nonZeroAssetOptions &&
            nonZeroAssetOptions?.length > 0 &&
            nonZeroAssetOptions?.map((ao, index) => {
              const amount = ao.amount;
              const sourceType = ao.sourceType;
              if (!amount || !sourceType) return null;

              return (
                <StyledItem
                  key={ao.assetItemGuid ?? `${sourceType}-${index}`}
                  padding={Spacing.M}
                  spacing={Spacing.S}
                  onClick={() => {
                    mutate(
                      {
                        analysisId,
                        versionId,
                        goalId,
                        data: {
                          amount: ao.remainingAmount || amount,
                          sourceType: sourceType as CreateAllocatedAmountBodySourceType,
                          assetItemGuid: ao.assetItemGuid,
                        },
                      },
                      {
                        onSuccess: () => {
                          queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAssetAmountQueryKey(analysisId, versionId) });
                          queryClient.invalidateQueries({ queryKey: getGetGoalModelingDetailQueryKey(analysisId, versionId, goalId) });
                          queryClient.invalidateQueries({ queryKey: getGetModelingRecapitulationQueryKey(analysisId, versionId) });
                          toast.success(t(Resources.AnalysisDetail.Modeling.AssetsAssigned));
                        },
                        onError: () => toast.error(t(Resources.Common.Error)),
                      },
                    );
                    setOpen(false);
                  }}
                >
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={500}>
                      {ao.labels?.name}
                    </Typography>
                    <Box display="flex">
                      <Typography variant="body2">{formatCurrencyWithoutCode(ao.remainingAmount?.value)}&nbsp;</Typography>
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="secondary">
                    {!!ao.labels?.clientLabel && `${ao.labels.clientLabel} • `}
                    {!!ao.labels?.institutionLabel && `${ao.labels.institutionLabel} • `}
                    {`${t(Resources.AnalysisDetail.Modeling.FromTotal)} ${formatCurrency(amount.value, amount.currencyCode)}`}
                  </Typography>
                </StyledItem>
              );
            })}
        </Stack>
      </Popper>
    </Box>
  );
};
